import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { AlaCarteItem, AlaCarteSection } from '@core/interfaces/api';
import { emptyItem, emptySection } from './alacarte-helpers';
import { InlineEdit } from './alacarte-inline-edit';

// ─── Sortable Item Row ─────────────────────────────────────────────────────────

function SortableItemRow({
  item,
  onUpdate,
  onDelete,
  autoFocus,
  isLeft = true,
}: {
  item: AlaCarteItem;
  onUpdate: (updated: AlaCarteItem) => void;
  onDelete: () => void;
  autoFocus?: boolean;
  isLeft?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
  };
  const hasDescription = item.description !== undefined && item.description !== null;
  const nameRef = useRef<{ startEditing: () => void } | null>(null);
  const descRef = useRef<{ startEditing: () => void } | null>(null);

  useEffect(() => {
    if (autoFocus) nameRef.current?.startEditing();
  }, [autoFocus]);

  return (
    <div ref={setNodeRef} style={style}>
      <Box
        sx={{
          position: 'relative',
          '&:hover .item-actions': { opacity: 1 },
          mb: 0.25,
        }}
      >
        <Box
          className="item-actions"
          sx={{ opacity: 0, transition: 'opacity 0.15s', display: 'flex', alignItems: 'center', position: 'absolute', left: -48, top: 0, zIndex: 1 }}
        >
          <Box {...attributes} {...listeners} sx={{ cursor: 'grab', color: 'text.disabled', display: 'flex' }}>
            <DragIndicatorIcon fontSize="small" />
          </Box>
          <IconButton size="small" onClick={onDelete} sx={{ color: 'error.main', p: 0.25 }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box>
          {!hasDescription ? (
            <div style={{ fontStyle: 'italic', fontSize: '18px', color: '#222', textAlign: isLeft ? 'center' : 'left', lineHeight: '1.4', fontFamily: 'Arial, Helvetica, sans-serif' }}>
              <InlineEdit
                value={item.name}
                onChange={(v) => onUpdate({ ...item, name: v })}
                style={{ fontStyle: 'italic', fontSize: '18px', color: '#222', textAlign: isLeft ? 'center' : 'left', fontFamily: 'Arial, Helvetica, sans-serif' }}
                placeholder="Item"
                multiline
                editingRef={nameRef}
              />
            </div>
          ) : (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '17px', textTransform: 'uppercase', letterSpacing: '0.3px', fontFamily: 'Arial, Helvetica, sans-serif' }}>
                <InlineEdit
                  value={item.name}
                  onChange={(v) => onUpdate({ ...item, name: v })}
                  style={{ fontWeight: 'bold', fontSize: '17px', textTransform: 'uppercase', letterSpacing: '0.3px', fontFamily: 'Arial, Helvetica, sans-serif' }}
                  placeholder="Item name"
                  onTab={() => descRef.current?.startEditing()}
                  editingRef={nameRef}
                />
              </div>
              <div style={{ fontStyle: 'italic', fontSize: '17px', color: '#333', lineHeight: '1.4', fontFamily: 'Arial, Helvetica, sans-serif' }}>
                <InlineEdit
                  value={item.description ?? ''}
                  onChange={(v) => onUpdate({ ...item, description: v })}
                  style={{ fontStyle: 'italic', fontSize: '17px', color: '#333', lineHeight: '1.4', fontFamily: 'Arial, Helvetica, sans-serif' }}
                  placeholder="Description"
                  multiline
                  editingRef={descRef}
                />
              </div>
            </div>
          )}
        </Box>
      </Box>
    </div>
  );
}

// ─── Add Item Dropdown ────────────────────────────────────────────────────────

function AddItemDropdown({ onAdd }: { onAdd: (withDescription: boolean) => void }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ mt: 0.5, fontSize: '11px', py: 0.25, color: 'text.secondary' }}
      >
        Add item
      </Button>
      <Popper open={!!anchorEl} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper elevation={4} sx={{ mt: 0.5 }}>
            <MenuList dense>
              <MenuItem onClick={() => { setAnchorEl(null); onAdd(false); }}>
                Item
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); onAdd(true); }}>
                Item with description
              </MenuItem>
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}

// ─── Sortable Section ──────────────────────────────────────────────────────────

function SortableSectionBlock({
  section,
  isLeft,
  onUpdate,
  onDelete,
  onAddItem,
  newItemId,
}: {
  section: AlaCarteSection;
  isLeft: boolean;
  onUpdate: (updated: AlaCarteSection) => void;
  onDelete: () => void;
  onAddItem: (withDescription: boolean) => void;
  newItemId?: string | null;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: section.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: '14px',
  };

  const updateItem = (idx: number, updated: AlaCarteItem) => {
    const items = [...section.items];
    items[idx] = updated;
    onUpdate({ ...section, items });
  };

  const deleteItem = (idx: number) => {
    const items = section.items.filter((_, i) => i !== idx);
    onUpdate({ ...section, items });
  };

  const handleItemsDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = section.items.findIndex((i) => i.id === active.id);
    const newIdx = section.items.findIndex((i) => i.id === over.id);
    onUpdate({ ...section, items: arrayMove(section.items, oldIdx, newIdx) });
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const headerStyle: React.CSSProperties = {
    fontSize: isLeft ? '28px' : '34px',
    fontFamily: '"Times New Roman", Times, serif',
    textAlign: isLeft ? 'center' : 'left',
    marginBottom: isLeft ? '6px' : '10px',
    lineHeight: '1.2',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Box sx={{ '&:hover .section-actions': { opacity: 1 }, position: 'relative' }}>
        <Box
          className="section-actions"
          sx={{ opacity: 0, transition: 'opacity 0.15s', position: 'absolute', top: 0, right: 0, display: 'flex', zIndex: 1 }}
        >
          <Box {...attributes} {...listeners} sx={{ cursor: 'grab', color: 'text.disabled', display: 'flex', alignItems: 'center' }}>
            <DragIndicatorIcon fontSize="small" />
          </Box>
          <IconButton size="small" onClick={onDelete} sx={{ color: 'error.main', p: 0.25 }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
        <div style={headerStyle}>
          <InlineEdit
            value={section.header}
            onChange={(v) => onUpdate({ ...section, header: v })}
            style={headerStyle}
            placeholder="Header"
          />
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleItemsDragEnd}>
          <SortableContext items={section.items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            {section.items.map((item, idx) => (
              <SortableItemRow
                key={item.id}
                item={item}
                onUpdate={(updated) => updateItem(idx, updated)}
                onDelete={() => deleteItem(idx)}
                autoFocus={item.id === newItemId}
                isLeft={isLeft}
              />
            ))}
          </SortableContext>
        </DndContext>
        <AddItemDropdown onAdd={onAddItem} />
      </Box>
    </div>
  );
}

// ─── Column Editor ─────────────────────────────────────────────────────────────

export function ColumnEditor({
  sections,
  isLeft,
  onSectionsChange,
}: {
  sections: AlaCarteSection[];
  isLeft: boolean;
  onSectionsChange: (sections: AlaCarteSection[]) => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor));
  const [newItemId, setNewItemId] = useState<string | null>(null);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex((s) => s.id === active.id);
    const newIdx = sections.findIndex((s) => s.id === over.id);
    onSectionsChange(arrayMove(sections, oldIdx, newIdx));
  };

  const updateSection = (idx: number, updated: AlaCarteSection) => {
    const next = [...sections];
    next[idx] = updated;
    onSectionsChange(next);
  };

  const deleteSection = (idx: number) => {
    onSectionsChange(sections.filter((_, i) => i !== idx));
  };

  const addItemToSection = (idx: number, withDescription: boolean) => {
    const item = emptyItem(withDescription);
    setNewItemId(item.id);
    const next = [...sections];
    next[idx] = { ...next[idx], items: [...next[idx].items, item] };
    onSectionsChange(next);
  };

  // Clear newItemId after it's been rendered and focused
  useEffect(() => {
    if (newItemId) {
      const timer = setTimeout(() => setNewItemId(null), 100);
      return () => clearTimeout(timer);
    }
  }, [newItemId]);

  return (
    <div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {sections.map((section, idx) => (
            <SortableSectionBlock
              key={section.id}
              section={section}
              isLeft={isLeft}
              onUpdate={(updated) => updateSection(idx, updated)}
              onDelete={() => deleteSection(idx)}
              onAddItem={(withDesc) => addItemToSection(idx, withDesc)}
              newItemId={newItemId}
            />
          ))}
        </SortableContext>
      </DndContext>
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onSectionsChange([...sections, emptySection()])}
        sx={{ mt: 0.5, fontSize: '11px', py: 0.25, color: 'text.secondary' }}
      >
        Add section
      </Button>
    </div>
  );
}
